import * as React from 'react';
import {
  NumberCellTemplate,
  getCharFromKey,
  inNumericKey,
  isAllowedOnNumberTypingKey,
  isCharAllowedOnNumberInput,
  isNavigationKey,
  keyCodes,
  type Compatible,
  type NumberCell,
} from '@silevis/reactgrid';

const parseLocaleNumber = (value: string, locale: string) => {
  const formatter = new Intl.NumberFormat(locale, {
    useGrouping: true,
    maximumFractionDigits: 2,
  });
  const parts = formatter.formatToParts(1234567.89);
  const decimal = parts.find((p) => p.type === 'decimal')?.value ?? '.';
  const group = parts.find((p) => p.type === 'group')?.value;

  let normalized = value.trim().replace(/[£]/g, '');
  if (group) {
    normalized = normalized.split(group).join('');
  }
  normalized = normalized.replace(decimal, '.');
  const num = Number(normalized);
  return Number.isFinite(num) ? num : NaN;
};

const getTextFromCharCode = (cellText: string) => {
  if (!cellText.length) return '';
  const code = cellText.charCodeAt(0);
  if ([keyCodes.DASH, keyCodes.FIREFOX_DASH, keyCodes.SUBTRACT].includes(code))
    return '-';
  if (code === keyCodes.COMMA) return ',';
  if ([keyCodes.PERIOD, keyCodes.DECIMAL].includes(code)) return '.';
  return cellText;
};

class CustomNumberCellTemplateV2 extends NumberCellTemplate {
  override render(
    cell: Compatible<NumberCell>,
    isInEditMode: boolean,
    onCellChanged: (cell: Compatible<NumberCell>, commit: boolean) => void,
  ): React.ReactNode {
    if (!isInEditMode) {
      const isValid = cell.validator?.(cell.value) ?? true;
      return !isValid && cell.errorMessage ? cell.errorMessage : cell.text;
    }

    const locale =
      cell.format?.resolvedOptions?.()?.locale ?? navigator.language;
    const format = new Intl.NumberFormat(locale, {
      useGrouping: false,
      maximumFractionDigits: 20,
    });
    const defaultValue = Number.isNaN(cell.value)
      ? getTextFromCharCode(cell.text)
      : format.format(cell.value);

    const isClipboardShortcut = (e: React.KeyboardEvent) =>
      (e.ctrlKey || e.metaKey) &&
      [keyCodes.KEY_V, keyCodes.KEY_C, keyCodes.KEY_X].includes(e.keyCode);

    const isSelectAllShortcut = (e: React.KeyboardEvent) =>
      (e.ctrlKey || e.metaKey) && e.keyCode === keyCodes.KEY_A;

    const handleChange = (value: string, commit: boolean) =>
      onCellChanged(
        this.getCompatibleCell({
          ...cell,
          value: parseLocaleNumber(value, locale),
        }),
        commit,
      );

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (isClipboardShortcut(e)) return e.stopPropagation();

      if (
        inNumericKey(e.keyCode) ||
        isNavigationKey(e.keyCode) ||
        isAllowedOnNumberTypingKey(e.keyCode) ||
        isSelectAllShortcut(e)
      ) {
        e.stopPropagation();
      }

      if (
        !isSelectAllShortcut(e) &&
        !inNumericKey(e.keyCode) &&
        !isNavigationKey(e.keyCode) &&
        !isCharAllowedOnNumberInput(getCharFromKey(e.key))
      ) {
        e.preventDefault();
      }
    };

    return (
      <input
        className="rg-input"
        inputMode="decimal"
        ref={(input) => {
          if (input) {
            input.focus();
            input.setSelectionRange(input.value.length, input.value.length);
          }
        }}
        defaultValue={defaultValue}
        onChange={(e) => handleChange(e.currentTarget.value, false)}
        onBlur={(e) => handleChange(e.currentTarget.value, true)}
        onKeyDown={handleKeyDown}
        onCopy={(e) => e.stopPropagation()}
        onCut={(e) => e.stopPropagation()}
        onPaste={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      />
    );
  }
}

export default CustomNumberCellTemplateV2;
